import { useCallback, useEffect, useState } from "react";
import DashboardShell from "@/components/layout/DashboardShell";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { VetBadge, PrescriptionDialog, MODE_LABEL, vfmt } from "@/components/vet/vetShared";
import { Stethoscope, Siren, Star, MapPin, Home, Video, FileText } from "lucide-react";

function BookDialog({ vet, emergency, open, setOpen, onDone }) {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setF({ mode: emergency ? "emergency" : "clinic" }); }, [open, emergency, vet?.vet_id]);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    if (!f.symptoms?.trim()) { toast.error("Please describe the problem/symptoms"); return; }
    setBusy(true);
    try {
      await api.post("/vet/appointments", {
        vet_id: vet.vet_id, mode: f.mode, date: f.date || "", time: f.time || "",
        animal_info: { type: f.animal_type || "", breed: f.breed || "", age: f.age || "", name: f.animal_name || "" },
        symptoms: f.symptoms, location: f.location || "",
      });
      toast.success(emergency ? "Emergency request sent!" : "Appointment requested");
      setOpen(false); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  if (!vet) return null;
  const fee = { online: vet.consultation_fee, clinic: vet.consultation_fee, home_visit: vet.home_visit_fee, emergency: vet.emergency_fee }[f.mode] || 0;
  const modes = emergency ? [["emergency", "EMERGENCY visit"]] : [
    ["clinic", `Clinic Visit (${vfmt(vet.consultation_fee)})`],
    ...(vet.online_available ? [["online", `Online Consultation (${vfmt(vet.consultation_fee)})`]] : []),
    ...(vet.home_visit_available ? [["home_visit", `Home Visit (${vfmt(vet.home_visit_fee)})`]] : []),
  ];
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-md max-h-[88vh] overflow-y-auto" data-testid="book-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {emergency ? <Siren className="w-5 h-5 text-destructive" /> : <Stethoscope className="w-5 h-5 text-primary" />}
            {emergency ? "Emergency Request" : "Book Appointment"}
          </DialogTitle>
          <DialogDescription>{vet.name} · {vet.specialization || "General"} · Fee {vfmt(fee)}</DialogDescription>
        </DialogHeader>
        <div>
          <Label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Visit Type</Label>
          <Select value={f.mode} onValueChange={(v) => set("mode", v)}>
            <SelectTrigger className="am-input mt-1" data-testid="book-mode"><SelectValue /></SelectTrigger>
            <SelectContent>{modes.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {!emergency && (
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Date</Label>
              <Input type="date" className="am-input mt-1" data-testid="book-date" value={f.date || ""} onChange={(e) => set("date", e.target.value)} /></div>
            <div><Label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Time</Label>
              <Input type="time" className="am-input mt-1" data-testid="book-time" value={f.time || ""} onChange={(e) => set("time", e.target.value)} /></div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div><Label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Animal Type *</Label>
            <Input className="am-input mt-1" placeholder="Buffalo / Cow / Goat" data-testid="book-animal-type" value={f.animal_type || ""} onChange={(e) => set("animal_type", e.target.value)} /></div>
          <div><Label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Breed / Age</Label>
            <Input className="am-input mt-1" placeholder="Murrah · 4 yrs" data-testid="book-breed" value={f.breed || ""} onChange={(e) => set("breed", e.target.value)} /></div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Problem / Symptoms *</Label>
          <Textarea rows={3} className="am-input mt-1" data-testid="book-symptoms" value={f.symptoms || ""} onChange={(e) => set("symptoms", e.target.value)} />
        </div>
        {(f.mode === "home_visit" || f.mode === "emergency") && (
          <div>
            <Label className="text-xs uppercase tracking-widest font-bold text-muted-foreground">Location (village / landmark) *</Label>
            <Input className="am-input mt-1" data-testid="book-location" value={f.location || ""} onChange={(e) => set("location", e.target.value)} />
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={() => setOpen(false)}>Cancel</Button>
          <Button className={emergency ? "rounded-full bg-destructive hover:bg-destructive/90" : "am-btn-primary"} disabled={busy} data-testid="book-save" onClick={save}>
            {emergency ? "Send Emergency Request" : "Request Appointment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function FindVet() {
  const [filters, setFilters] = useState({});
  const [vets, setVets] = useState([]);
  const [appts, setAppts] = useState([]);
  const [rxs, setRxs] = useState([]);
  const [bookFor, setBookFor] = useState(null);
  const [emergencyMode, setEmergencyMode] = useState(false);
  const [rxView, setRxView] = useState(null);

  const search = useCallback(() => {
    const params = {};
    if (filters.area) params.area = filters.area;
    if (filters.specialization) params.specialization = filters.specialization;
    ["emergency", "home_visit", "online"].forEach((k) => { if (filters[k]) params[k] = true; });
    api.get("/vet/search", { params }).then((r) => setVets(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Search failed"));
  }, [filters]);
  const loadMine = useCallback(() => {
    api.get("/vet/my/appointments").then((r) => setAppts(r.data)).catch(() => {});
    api.get("/vet/my/prescriptions").then((r) => setRxs(r.data)).catch(() => {});
  }, []);
  useEffect(() => { search(); }, [search]);
  useEffect(() => { loadMine(); }, [loadMine]);

  const cancel = async (a) => {
    try { await api.post(`/vet/appointments/${a.appointment_id}/cancel`); toast.success("Cancelled"); loadMine(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const confirm = async (a) => {
    try { await api.post(`/vet/appointments/${a.appointment_id}/confirm`); toast.success("Confirmed new time"); loadMine(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const openEmergency = () => {
    setFilters({ emergency: true });
    setEmergencyMode(true);
    toast.info("Showing emergency-ready vets. Pick one to send the request.");
  };

  const Toggle = ({ k, label, icon: Icon }) => (
    <button data-testid={`fv-filter-${k}`}
      className={`px-3 py-1.5 rounded-full text-xs font-bold border flex items-center gap-1.5 ${filters[k] ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}
      onClick={() => setFilters({ ...filters, [k]: !filters[k] })}>
      {Icon && <Icon className="w-3.5 h-3.5" />} {label}
    </button>
  );

  return (
    <DashboardShell title="Find Vet" subtitle="Verified veterinary doctors for your animals"
      action={<Button className="rounded-full bg-destructive hover:bg-destructive/90 text-white" data-testid="fv-emergency-btn" onClick={openEmergency}>
        <Siren className="w-4 h-4 mr-1.5" /> Emergency Vet
      </Button>}>
      <div className="space-y-6" data-testid="find-vet-page">
        <div className="am-card p-4 flex flex-wrap gap-2 items-center" data-testid="fv-filters">
          <Input className="am-input h-9 text-sm w-44" placeholder="Area (e.g. Mathura)" data-testid="fv-area"
            value={filters.area || ""} onChange={(e) => setFilters({ ...filters, area: e.target.value })} />
          <Input className="am-input h-9 text-sm w-44" placeholder="Specialization" data-testid="fv-spec"
            value={filters.specialization || ""} onChange={(e) => setFilters({ ...filters, specialization: e.target.value })} />
          <Toggle k="emergency" label="Emergency" icon={Siren} />
          <Toggle k="home_visit" label="Home Visit" icon={Home} />
          <Toggle k="online" label="Online" icon={Video} />
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="fv-results">
          {vets.map((v) => (
            <div key={v.vet_id} className="am-card p-5 flex flex-col gap-2" data-testid={`fv-vet-${v.vet_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-display text-lg font-extrabold">{v.name}</div>
                  <div className="text-xs text-muted-foreground">{v.qualification}{v.specialization ? ` · ${v.specialization}` : ""} · {v.experience_years} yrs</div>
                </div>
                <span className="flex items-center gap-1 text-sm font-bold text-amber-600"><Star className="w-4 h-4 fill-amber-400 text-amber-400" />{v.rating || "New"}</span>
              </div>
              <div className="text-xs text-muted-foreground flex items-center gap-1"><MapPin className="w-3.5 h-3.5 shrink-0" />{(v.service_areas || []).join(", ") || v.address || "—"}</div>
              <div className="flex flex-wrap gap-1.5 text-[11px] font-bold">
                <span className="px-2 py-0.5 rounded-full bg-muted">Consult {vfmt(v.consultation_fee)}</span>
                {v.home_visit_available && <span className="px-2 py-0.5 rounded-full bg-muted">Home {vfmt(v.home_visit_fee)}</span>}
                {v.emergency_available && <span className="px-2 py-0.5 rounded-full bg-destructive/10 text-destructive">Emergency {vfmt(v.emergency_fee)}</span>}
                {v.online_available && <span className="px-2 py-0.5 rounded-full bg-muted">Online</span>}
              </div>
              {v.availability_note && <div className="text-xs text-muted-foreground">{v.availability_note}</div>}
              <Button className={`mt-auto rounded-full ${emergencyMode && v.emergency_available ? "bg-destructive hover:bg-destructive/90 text-white" : "am-btn-primary"}`}
                data-testid={`fv-book-${v.vet_id}`} onClick={() => setBookFor({ ...v, _emergency: emergencyMode && v.emergency_available })}>
                {emergencyMode && v.emergency_available ? "Send Emergency Request" : "Book Appointment"}
              </Button>
            </div>
          ))}
          {vets.length === 0 && <p className="text-sm text-muted-foreground col-span-full p-6 text-center">No verified vets match these filters yet.</p>}
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <div className="am-card p-5" data-testid="fv-my-appointments">
            <h2 className="font-display text-lg font-bold mb-3">My Appointments</h2>
            {appts.length === 0 && <p className="text-sm text-muted-foreground">No appointments yet.</p>}
            {appts.map((a) => (
              <div key={a.appointment_id} className="border-b last:border-0 py-2.5 text-sm" data-testid={`fv-appt-${a.appointment_id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div>
                    <b>{a.vet_name}</b> · {MODE_LABEL[a.mode]} · {a.date} {a.time}
                    <div className="text-xs text-muted-foreground">{[a.animal_info?.type, a.animal_info?.breed].filter(Boolean).join(" ")} · {a.symptoms?.slice(0, 50)}</div>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <VetBadge status={a.status} />
                    {a.status === "RESCHEDULED" && <Button size="sm" className="am-btn-primary rounded-full text-xs" data-testid={`fv-confirm-${a.appointment_id}`} onClick={() => confirm(a)}>Accept New Time</Button>}
                    {["REQUESTED", "ACCEPTED", "CONFIRMED", "RESCHEDULED"].includes(a.status) && (
                      <Button size="sm" variant="outline" className="rounded-full text-xs" data-testid={`fv-cancel-${a.appointment_id}`} onClick={() => cancel(a)}>Cancel</Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="am-card p-5" data-testid="fv-prescriptions">
            <h2 className="font-display text-lg font-bold mb-3">My Prescriptions</h2>
            {rxs.length === 0 && <p className="text-sm text-muted-foreground">Prescriptions appear here after a consultation.</p>}
            {rxs.map((rx) => (
              <div key={rx.prescription_id} className="flex items-center justify-between gap-2 border-b last:border-0 py-2.5 text-sm">
                <div><b>{rx.vet_details?.name}</b> · {rx.diagnosis}<div className="text-xs text-muted-foreground">{(rx.created_at || "").slice(0, 10)} · {[rx.animal_info?.type, rx.animal_info?.breed].filter(Boolean).join(" ")}</div></div>
                <Button size="sm" variant="outline" className="rounded-full text-xs" data-testid={`fv-rx-${rx.prescription_id}`} onClick={() => setRxView(rx)}>
                  <FileText className="w-3.5 h-3.5 mr-1" /> View
                </Button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <BookDialog vet={bookFor} emergency={!!bookFor?._emergency} open={!!bookFor}
        setOpen={() => { setBookFor(null); }} onDone={() => { loadMine(); setEmergencyMode(false); setFilters({}); }} />
      <PrescriptionDialog rx={rxView} open={!!rxView} setOpen={() => setRxView(null)} />
    </DashboardShell>
  );
}
